/**
 * PURPOSE: Catálogo frontend de eventos y posiciones de HandballBeach.
 * RESPONSIBILITIES:
 *  - Sí: mapear label UI -> eventTypeId/posTypeId.
 *  - Sí: declarar qué acciones requieren lanzamiento, arquero rival o son eventos de equipo.
 *  - No: no persiste eventos ni calcula marcador.
 * COLLABORATORS:
 *  - services/handballBeachMatchEventService.js
 *  - components/ActionSidebarHandballBeach.jsx
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/HandballBeach/constants/handballBeachEventTypes.js
 */

export const HANDBALL_BEACH_EVENT_TYPE = Object.freeze({
  AMARILLA: 1,
  ROJA: 2,
  EXCLUSION: 3,
  LANZAMIENTO_NORMAL_GOL: 4,
  LANZAMIENTO_NORMAL_TAPO: 5,
  LANZAMIENTO_NORMAL_FUERA: 6,
  LANZAMIENTO_NORMAL_PALO: 7,
  LANZAMIENTO_360_GOL: 8,
  LANZAMIENTO_360_TAPO: 9,
  LANZAMIENTO_360_FUERA: 10,
  LANZAMIENTO_360_PALO: 11,
  "LANZAMIENTO_IN-FLIGHT_GOL": 12,
  "LANZAMIENTO_IN-FLIGHT_TAPO": 13,
  "LANZAMIENTO_IN-FLIGHT_FUERA": 14,
  "LANZAMIENTO_IN-FLIGHT_PALO": 15,
  LANZAMIENTO_ESPECIALISTA_GOL: 16,
  LANZAMIENTO_ESPECIALISTA_TAPO: 17,
  LANZAMIENTO_ESPECIALISTA_FUERA: 18,
  LANZAMIENTO_ESPECIALISTA_PALO: 19,
  TIMEOUT: 20,
  START_SET: 21,
  END_SET: 22,
  END_MATCH: 23,
});

export const HANDBALL_BEACH_EVENT_LABEL = Object.freeze(
  Object.fromEntries(Object.entries(HANDBALL_BEACH_EVENT_TYPE).map(([label, id]) => [id, label]))
);

export const HANDBALL_BEACH_POS_TYPE = Object.freeze({
  CENTRAL: 1,
  LAT_DER: 2,
  EXT_DER: 3,
  LAT_IZQ: 4,
  EXT_IZQ: 5,
  PIVOT: 6,
  PENAL_7M: 7,
  LANZ_9M: 8,
  ARCO_ARCO: 9,
});

export const HANDBALL_BEACH_POS_LABEL = Object.freeze(
  Object.fromEntries(Object.entries(HANDBALL_BEACH_POS_TYPE).map(([label, id]) => [id, label]))
);

export const HANDBALL_BEACH_EVENT_BUTTONS = [
  { label: "AMARILLA", color: "bg-yellow-400 text-black" },
  { label: "ROJA", color: "bg-red-600 text-white" },
  { label: "EXCLUSION", color: "bg-orange-500 text-white" },
  { label: "FUERA", color: "bg-orange-500 text-white" },
  { label: "PALO", color: "bg-yellow-500 text-black" },
  { label: "TAPO", color: "bg-purple-600 text-white" },
  { label: "GOL", color: "bg-green-600 text-white" },
];

export const HANDBALL_BEACH_SHOT_TYPES = [
  "NORMAL",
  "360",
  "IN-FLIGHT",
  "ESPECIALISTA",
];

export const HANDBALL_BEACH_SHOT_RESULTS = ["GOL", "TAPO", "FUERA", "PALO"];
export const HANDBALL_BEACH_DIRECT_ACTIONS = ["AMARILLA", "ROJA", "EXCLUSION"];
export const HANDBALL_BEACH_SHOT_REQUIRED_ACTIONS = HANDBALL_BEACH_SHOT_RESULTS;
export const HANDBALL_BEACH_GOALKEEPER_REQUIRED_ACTIONS = ["GOL", "TAPO"];
export const HANDBALL_BEACH_GOAL_EVENT_TYPE_IDS = Object.freeze([
  HANDBALL_BEACH_EVENT_TYPE.LANZAMIENTO_NORMAL_GOL,
  HANDBALL_BEACH_EVENT_TYPE.LANZAMIENTO_360_GOL,
  HANDBALL_BEACH_EVENT_TYPE["LANZAMIENTO_IN-FLIGHT_GOL"],
  HANDBALL_BEACH_EVENT_TYPE.LANZAMIENTO_ESPECIALISTA_GOL,
]);
export const HANDBALL_BEACH_GOAL_POINT_VALUE_BY_EVENT_TYPE_ID = Object.freeze({
  [HANDBALL_BEACH_EVENT_TYPE.LANZAMIENTO_NORMAL_GOL]: 1,
  [HANDBALL_BEACH_EVENT_TYPE.LANZAMIENTO_360_GOL]: 2,
  [HANDBALL_BEACH_EVENT_TYPE["LANZAMIENTO_IN-FLIGHT_GOL"]]: 2,
  [HANDBALL_BEACH_EVENT_TYPE.LANZAMIENTO_ESPECIALISTA_GOL]: 2,
});
export const HANDBALL_BEACH_GOAL_EVENT_TYPE_ID = HANDBALL_BEACH_GOAL_EVENT_TYPE_IDS[0];
export const HANDBALL_BEACH_TIMEOUT_EVENT_TYPE_ID = HANDBALL_BEACH_EVENT_TYPE.TIMEOUT;
export const HANDBALL_BEACH_START_SET_EVENT_TYPE_ID = HANDBALL_BEACH_EVENT_TYPE.START_SET;
export const HANDBALL_BEACH_END_SET_EVENT_TYPE_ID = HANDBALL_BEACH_EVENT_TYPE.END_SET;
export const HANDBALL_BEACH_END_MATCH_EVENT_TYPE_ID = HANDBALL_BEACH_EVENT_TYPE.END_MATCH;
export const HANDBALL_BEACH_LIFECYCLE_EVENT_TYPE_IDS = Object.freeze([
  HANDBALL_BEACH_START_SET_EVENT_TYPE_ID,
  HANDBALL_BEACH_END_SET_EVENT_TYPE_ID,
  HANDBALL_BEACH_END_MATCH_EVENT_TYPE_ID,
]);
export const HANDBALL_BEACH_SET_NUMBERS = Object.freeze([1, 2, 3]);
export const HANDBALL_BEACH_MAX_SETS = 3;
export const HANDBALL_BEACH_SETS_TO_WIN = 2;
export const HANDBALL_BEACH_SHOOTOUT_SET_NUMBER = 3;
export const HANDBALL_BEACH_SHOOTOUT_BASE_ROUNDS = 5;
export const HANDBALL_BEACH_SHOOTOUT_ARCO_ARCO_POSITION = "ARCO_ARCO";
export const HANDBALL_BEACH_SHOOTOUT_GOAL_POINT_VALUE_BY_EVENT_TYPE_ID = Object.freeze({
  [HANDBALL_BEACH_EVENT_TYPE.LANZAMIENTO_NORMAL_GOL]: 1,
  [HANDBALL_BEACH_EVENT_TYPE.LANZAMIENTO_360_GOL]: 2,
  [HANDBALL_BEACH_EVENT_TYPE["LANZAMIENTO_IN-FLIGHT_GOL"]]: 2,
  [HANDBALL_BEACH_EVENT_TYPE.LANZAMIENTO_ESPECIALISTA_GOL]: 2,
});

//CONFIGURATION
export const HANDBALL_BEACH_ALLOW_EVENTS_IN_CLOSED_SETS = true;
export const HANDBALL_BEACH_ENABLE_GOALKEEPER_CLASSIFICATION = true;
export const HANDBALL_BEACH_SHOW_END_MATCH_BUTTON = false;


// Detecta eventos de ciclo de set/partido.
export function isHandballBeachLifecycleEventTypeId(eventTypeId) {
  return HANDBALL_BEACH_LIFECYCLE_EVENT_TYPE_IDS.includes(Number(eventTypeId));
}

export function isHandballBeachStartSetEventTypeId(eventTypeId) { 
  return Number(eventTypeId) === Number(HANDBALL_BEACH_START_SET_EVENT_TYPE_ID);
}

export function isHandballBeachEndSetEventTypeId(eventTypeId) {
  return Number(eventTypeId) === Number(HANDBALL_BEACH_END_SET_EVENT_TYPE_ID);
}

export function isHandballBeachEndMatchEventTypeId(eventTypeId) {
  return Number(eventTypeId) === Number(HANDBALL_BEACH_END_MATCH_EVENT_TYPE_ID);
}
export const HANDBALL_BEACH_EXCLUSION_2_EVENT_TYPE_ID = HANDBALL_BEACH_EVENT_TYPE.EXCLUSION;
export const HANDBALL_BEACH_TIMEOUT_ALLOWED_PERIODS = HANDBALL_BEACH_SET_NUMBERS;
export const HANDBALL_BEACH_TIMEOUT_MAX_PER_MATCH = 3;
export const HANDBALL_BEACH_TIMEOUT_MAX_PER_PERIOD = 1;
export const HANDBALL_BEACH_EXCLUSION_SECONDS = 120;

// Indica si la acción necesita tipo de lanzamiento antes de guardar.
export function handballBeachActionRequiresShotType(action) {
  return HANDBALL_BEACH_SHOT_REQUIRED_ACTIONS.includes(String(action || ""));
}

// Indica si la acción necesita arquero rival actual seleccionado.
export function handballBeachActionRequiresGoalkeeper(action) {
  return HANDBALL_BEACH_GOALKEEPER_REQUIRED_ACTIONS.includes(String(action || ""));
}

export function buildHandballBeachShotEventTypeKey(shotType, result) {
  if (!shotType || !result) return null;
  return `LANZAMIENTO_${shotType}_${result}`;
}

export function getHandballBeachEventTypeId({ action, shotType }) {
  const safeAction = String(action || "");
  if (HANDBALL_BEACH_SHOT_RESULTS.includes(safeAction)) {
    const key = buildHandballBeachShotEventTypeKey(shotType, safeAction);
    return HANDBALL_BEACH_EVENT_TYPE[key] || null;
  }
  return HANDBALL_BEACH_EVENT_TYPE[safeAction] || null;
}

// Detecta eventos de lanzamiento para mostrar posición en historial/modal.
export function isHandballBeachShotEventTypeId(eventTypeId) {
  const id = Number(eventTypeId);
  return Object.entries(HANDBALL_BEACH_EVENT_TYPE).some(
    ([label, value]) => label.startsWith("LANZAMIENTO_") && Number(value) === id
  );
}

// Detecta tiempo muerto de equipo.
export function isHandballBeachTimeoutEventTypeId(eventTypeId) {
  return Number(eventTypeId) === Number(HANDBALL_BEACH_TIMEOUT_EVENT_TYPE_ID);
}

// La exclusión se marca visualmente sin mostrar contador público.
export function isHandballBeachExclusion2EventTypeId() {
  return false;
}
